// EligibilityRuleMaster.js
import { Fragment, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";

export default function EligibilityRuleMaster() {
  const [open, setOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(null);

  // Rules state
  const [rules, setRules] = useState([
    {
      field: "Gross",
      operator: "<",
      value: "30000",
      structure: "Below 30K - Male",
      active: true,
    },
    {
      field: "Gender",
      operator: "=",
      value: "Female",
      structure: "Below 30K - Female",
      active: true,
    },
  ]);

  const [form, setForm] = useState({
    field: "",
    operator: "",
    value: "",
    structure: "",
    active: true,
  });

  const fields = ["Gross", "Gender"]; // later you can extend to Department, Location, etc.
  const operators = ["<", "<=", "=", ">=", "!="];

  const structures = [
    "Below 30K - Male",
    "Below 30K - Female",
    "Above 30K",
  ]; // fetch from PayStructureMaster

  const handleSave = () => {
    if (editIndex !== null) {
      const updated = [...rules];
      updated[editIndex] = form;
      setRules(updated);
    } else {
      setRules([...rules, form]);
    }
    setForm({ field: "", operator: "", value: "", structure: "", active: true });
    setEditIndex(null);
    setOpen(false);
  };

  const handleEdit = (index) => {
    setForm(rules[index]);
    setEditIndex(index);
    setOpen(true);
  };

  const handleDelete = (index) => {
    setRules(rules.filter((_, i) => i !== index));
  };

  const renderCondition = (rule) =>
    `${rule.field} ${rule.operator} ${rule.value}`;

  return (
    <div className="bg-white dark:bg-gray-900 p-4 font-poppins rounded-2xl">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Eligibility / Rule Master</h2>
        <button
          onClick={() => setOpen(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700"
        >
          + Add Rule
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto bg-white dark:bg-gray-900 shadow-md rounded-lg font-poppins">
        <table className="min-w-full table-auto md:table-fixed text-sm">
          <thead className="bg-gray-100 dark:bg-gray-800 dark:text-white">
            <tr>
              <th className="py-2 px-4 text-left  font-semibold text-xs md:text-sm text-black dark:text-white whitespace-nowrap">Condition</th>
              <th className="py-2 px-4 text-left  font-semibold text-xs md:text-sm text-black dark:text-white whitespace-nowrap">Pay Structure</th>
              <th className="py-2 px-4 text-left  font-semibold text-xs md:text-sm text-black dark:text-white whitespace-nowrap">Active</th>
              <th className="py-2 px-4 text-center  font-semibold text-xs md:text-sm text-black dark:text-white whitespace-nowrap">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rules.map((r, i) => (
              <tr key={i} className="border-b dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-950 dark:hover:text-white">
                <td className="py-2 px-4 text-xs dark:text-white text-left truncate md:text-sm whitespace-nowrap">{renderCondition(r)}</td>
                <td className="py-2 px-4 text-xs dark:text-white text-left truncate md:text-sm whitespace-nowrap">{r.structure}</td>
                <td className="py-2 px-4 text-xs dark:text-white text-left truncate md:text-sm whitespace-nowrap">{r.active ? "✓" : "✗"}</td>
                <td className="px-4 py-2 flex items-center justify-center gap-2">
                  <button
                    onClick={() => handleEdit(i)}
                    className="px-2 py-1 text-yellow-500 hover:underline hover:underline-offset-4"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(i)}
                    className="px-2 py-1 text-red-600 hover:underline hover:underline-offset-4"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      <Transition appear show={open} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setOpen(false)}>
          <div className="fixed inset-0 flex items-center justify-center p-4 bg-black/25">
            <Dialog.Panel className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg">
              <Dialog.Title className="text-lg font-medium text-gray-900">
                {editIndex !== null ? "Edit Rule" : "Add Rule"}
              </Dialog.Title>

              <div className="mt-4 space-y-3">
                {/* Field */}
                <select
                  value={form.field}
                  onChange={(e) => setForm({ ...form, field: e.target.value })}
                  className="w-full border p-2 rounded"
                >
                  <option value="">Select Field</option>
                  {fields.map((f, idx) => (
                    <option key={idx} value={f}>
                      {f}
                    </option>
                  ))}
                </select>

                {/* Operator */}
                <select
                  value={form.operator}
                  onChange={(e) => setForm({ ...form, operator: e.target.value })}
                  className="w-full border p-2 rounded"
                >
                  <option value="">Select Operator</option>
                  {operators.map((op, idx) => (
                    <option key={idx} value={op}>
                      {op}
                    </option>
                  ))}
                </select>

                {/* Value: dropdown if Gender, otherwise free text */}
                {form.field === "Gender" ? (
                  <select
                    value={form.value}
                    onChange={(e) => setForm({ ...form, value: e.target.value })}
                    className="w-full border p-2 rounded"
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                ) : (
                  <input
                    type="text"
                    placeholder="Enter value"
                    value={form.value}
                    onChange={(e) => setForm({ ...form, value: e.target.value })}
                    className="w-full border p-2 rounded"
                  />
                )}

                {/* Pay Structure */}
                <select
                  value={form.structure}
                  onChange={(e) => setForm({ ...form, structure: e.target.value })}
                  className="w-full border p-2 rounded"
                >
                  <option value="">Select Pay Structure</option>
                  {structures.map((s, idx) => (
                    <option key={idx} value={s}>
                      {s}
                    </option>
                  ))}
                </select>

                {/* Active */}
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={form.active}
                    onChange={(e) => setForm({ ...form, active: e.target.checked })}
                  />
                  Active
                </label>
              </div>

              <div className="mt-4 flex justify-end gap-2">
                <button
                  onClick={() => setOpen(false)}
                  className="px-4 py-2 rounded-md border bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="px-4 py-2 rounded-md bg-blue-600 text-white"
                >
                  Save
                </button>
              </div>
            </Dialog.Panel>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
}
